import debounce from 'lodash.debounce';
import React, { ChangeEvent } from 'react';
import Menu from '@mui/material/Menu';
import SvgIcon from '@mui/material/SvgIcon';
import { InputBaseProps } from '@mui/material/InputBase';

import {
    StyledCountryButton,
    StyledMenuItem,
    StyledMenuSearch,
    StyledPhoneCode,
    StyledInput,
    StyledClearButton,
    StyledFlagIcon,
} from './phone-input.style';

import { Input } from '../../atoms/input/input';
import { FormControl } from '../../atoms/form-control/form-control';

import { __ } from '../../../utils/translation-service';
import crossIcon from '../../../../public/static/icons/cross.svg';
import arrowUp from '../../../../public/static/icons/arrowUpV2.svg';
import { getFlagEmoji } from '../country-select/country-select';
import allCountries from '../country-select/countries.json';

type Country = {
    name: string;
    phone_code: string;
    code: string;
};

const PHONE_INPUT_ID = 'dreamlines-phone-input';
const ITEM_HEIGHT = 34;
const VISIBLE_ITEMS = 9.5;

const PhoneCodeSelect = (props: {
    code: string;
    phoneCode: string;
    onChange: (country: Country) => void;
}) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [open, setOpen] = React.useState(false);
    const [searchString, setSearchString] = React.useState('');
    const [searchResults, setSearchResults] = React.useState<Array<Country>>([]);
    const searchInputRef = React.useRef<HTMLInputElement | null>(null);

    const defaultOptions = React.useMemo(() => {
        return allCountries.map((c) => ({
            ...c,
            name: __(c.code),
        }));
    }, []);

    const handleClick = () => {
        setOpen(true);
    };

    const handleClose = () => {
        searchInputRef.current = null;
        setOpen(false);
        setTimeout(() => {
            setSearchString('');
            setSearchResults([]);
        }, 0);
    };

    const handleSelect = (selectedCountry: Country) => {
        if (selectedCountry) props.onChange(selectedCountry);
        handleClose();
    };

    const clearSearch = () => {
        if (searchInputRef.current) {
            searchInputRef.current.value = '';
        }
        setSearchString('');
        setSearchResults([]);
    };

    const handleSearch = debounce((e: ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchString(query);

        if (!query && searchResults.length) {
            setSearchResults([]);
            return;
        }

        const searchResult = defaultOptions.filter((option) =>
            option.name.toLocaleLowerCase().includes(query.toLowerCase()),
        );

        if (searchResult.length) {
            setSearchResults(searchResult);
        }
    }, 300);

    React.useEffect(() => {
        const el = document.getElementById(PHONE_INPUT_ID);

        if (el?.parentElement) {
            setAnchorEl(el?.parentElement);
        }
    }, []);

    return (
        <>
            <StyledCountryButton
                isActive={open}
                id="country-phones-button"
                aria-controls={open ? 'countries-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
            >
                <StyledFlagIcon>{getFlagEmoji(props.code)}</StyledFlagIcon>

                <SvgIcon
                    inheritViewBox
                    component={arrowUp}
                    sx={{
                        width: '12px',
                    }}
                />
            </StyledCountryButton>

            <StyledPhoneCode> {props.phoneCode} </StyledPhoneCode>

            <Menu
                id="phone-code-select-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        marginTop: '10px',
                        maxHeight: ITEM_HEIGHT * VISIBLE_ITEMS,
                        width: anchorEl ? anchorEl?.offsetWidth : undefined,
                    },
                }}
            >
                <StyledMenuSearch disableRipple>
                    <FormControl fullWidth>
                        <Input
                            fullWidth
                            inputRef={(input: HTMLInputElement) => {
                                searchInputRef.current = input;
                            }}
                            size="small"
                            placeholder={`${__('searchPlaceHolder')}`}
                            onChange={handleSearch}
                            endAdornment={
                                searchString ? (
                                    <StyledClearButton onClick={clearSearch}>
                                        <SvgIcon
                                            inheritViewBox
                                            component={crossIcon}
                                            sx={{
                                                width: '14px',
                                                height: '14px',
                                            }}
                                        />
                                    </StyledClearButton>
                                ) : null
                            }
                        />
                    </FormControl>
                </StyledMenuSearch>

                {(searchResults.length ? searchResults : defaultOptions).map((country) => (
                    <StyledMenuItem
                        isSelected={country.code === props.code}
                        disableRipple
                        key={country.name}
                        onClick={() => handleSelect(country)}
                    >
                        <span> {getFlagEmoji(country.code)} </span>
                        <span> {country.name} </span>
                        <span> {country.phone_code} </span>
                    </StyledMenuItem>
                ))}
            </Menu>
        </>
    );
};

interface IPhoneSelectProps extends Omit<InputBaseProps, 'onChange' | 'value' | 'defaultValue'> {
    onChange?: (
        phoneNumber: string,
        phoneData: {
            code: string;
            number: string;
        },
    ) => void;

    defaultValue?: string;
    countryCode?: string;
}

export const PhoneInput = ({
    onChange,
    countryCode,
    defaultValue,
    ...props
}: IPhoneSelectProps) => {
    const isCountryCodeNumeric = countryCode?.startsWith('+');

    const initialCountry = countryCode
        ? allCountries.find((e) =>
              isCountryCodeNumeric ? e.phone_code === countryCode : e.code === countryCode,
          )
        : null;

    const [phoneCountryCode, setCountryCode] = React.useState(initialCountry?.code || '');
    const [phoneCode, setPhoneCode] = React.useState(initialCountry?.phone_code || '');

    const [phoneNumber, setPhoneNumber] = React.useState(defaultValue || '');

    const handleSelectedCodeChange = (country: Country) => {
        setCountryCode(country.code);
        setPhoneCode(country.phone_code);

        const fullPhone = phoneNumber.length ? `${country.phone_code}${phoneNumber}` : '';

        onChange?.(fullPhone, {
            code: country.phone_code,
            number: phoneNumber,
        });
    };

    const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
        const phoneNumber = e.target.value.trim().replace(/\D/g, '');
        setPhoneNumber(phoneNumber);
        const fullPhone = phoneNumber.length ? `${phoneCode}${phoneNumber}` : '';
        onChange?.(fullPhone, {
            code: phoneCode,
            number: phoneNumber,
        });
    };

    return (
        <StyledInput
            {...props}
            autoComplete="off"
            id={PHONE_INPUT_ID}
            onChange={handlePhoneChange}
            value={phoneNumber}
            startAdornment={
                <PhoneCodeSelect
                    phoneCode={phoneCode}
                    code={phoneCountryCode}
                    onChange={handleSelectedCodeChange}
                />
            }
        />
    );
};
